import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Select,
  MenuItem,
  FormControl,
  Grid
} from '@mui/material';
import EditableItemList from './common/EditableItemList';
import { useCV } from '../contexts/CVContext';
import { cvManagementApi } from '../../../../services/api/cvManagementApi';

const Languages = () => {
  const { isDataReady, cvData, updateLanguages, readonly } = useCV();
  
  const [languageLevels, setLanguageLevels] = useState([]);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Initialize with empty languages array
  const [languages, setLanguages] = useState([]);
  
  const [expandedIndex, setExpandedIndex] = useState(null);
  
  // Fetch language levels and available languages
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [levelsResponse, languagesResponse] = await Promise.all([
          cvManagementApi.getAllLanguageLevels(),
          cvManagementApi.getAllLanguages()
        ]);
        
        setLanguageLevels(levelsResponse || []);
        setAvailableLanguages(languagesResponse || []);
      } catch (error) {
        console.error('Error fetching language data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Update local state when context data changes, but only on initial load
  useEffect(() => {
    if (cvData.languages?.length > 0 && isDataReady) {
      setLanguages(cvData.languages);
    }
  }, [cvData.languages, isDataReady]);
  
  // Custom handler to update both local state and context
  const handleLanguagesChange = (newLanguages) => {
    // Update the order property for each language
    const orderedLanguages = newLanguages.map((lang, index) => ({
      ...lang,
      order: index + 1
    }));
    
    setLanguages(orderedLanguages);
    updateLanguages(orderedLanguages);
  };
  
  // Function to create a new empty language
  const createNewLanguage = () => ({
    id: null,
    language: null,
    level: null,
    order: languages.length + 1
  });

  // Function to render the summary of a language (collapsed view)
  const renderLanguageSummary = (language) => (
    <>
      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
        {language.language?.name || '(Not specified)'}
      </Typography>
      {language.level?.level && (
        <Typography variant="body2" color="text.secondary">
          {`${language.level.level} (${language.level.description})`}
        </Typography>
      )}
    </>
  );

  // Function to render the edit form for a language (expanded view)
  const renderLanguageEditForm = (language, onChange) => (
    <Grid 
      container 
      spacing={2}
      sx={{
        width: '100%',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(2, 1fr)'
        },
        gap: '16px',
        '& .MuiGrid-item': {
          padding: '0',
          width: '100%'
        }
      }}
    >
      <Grid item>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Language <span style={{ color: 'red' }}>*</span>
          </Typography>
          <FormControl fullWidth size="small" required>
            <Select
              value={language.language?.id || ''}
              onChange={(e) => {
                const selectedLanguage = availableLanguages.find(lang => lang.id === e.target.value);
                onChange({
                  ...language,
                  language: {
                    id: selectedLanguage.id,
                    name: selectedLanguage.name
                  }
                });
              }}
              displayEmpty
              disabled={readonly}
              sx={{ backgroundColor: 'white' }}
            >
              <MenuItem value="" disabled>
                <em>Select language</em>
              </MenuItem>
              {availableLanguages.map((lang) => (
                <MenuItem key={lang.id} value={lang.id}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Grid>
      
      <Grid item>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Level <span style={{ color: 'red' }}>*</span>
          </Typography>
          <FormControl fullWidth size="small" required>
            <Select
              value={language.level?.id || ''}
              onChange={(e) => {
                const selectedLevel = languageLevels.find(level => level.id === e.target.value);
                onChange({
                  ...language,
                  level: {
                    id: selectedLevel.id,
                    level: selectedLevel.level,
                    description: selectedLevel.description
                  }
                });
              }}
              displayEmpty
              disabled={readonly}
              sx={{ backgroundColor: 'white' }}
            >
              <MenuItem value="" disabled>
                <em>Select level</em>
              </MenuItem>
              {languageLevels.map((level) => (
                <MenuItem key={level.id} value={level.id}>
                  {`${level.level} (${level.description})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Grid>
    </Grid>
  );

  return (
    <EditableItemList
      items={languages}
      setItems={handleLanguagesChange}
      expandedIndex={expandedIndex}
      setExpandedIndex={setExpandedIndex}
      createNewItem={createNewLanguage}
      renderSummary={renderLanguageSummary}
      renderEditForm={renderLanguageEditForm}
      addButtonText={languages.length > 0 ? "Add one more language" : "Add a language"}
      emptyState={{
        title: "You haven't added any languages yet.",
        description: "Add any relevant languages you're proficient in."
      }}
      itemName="language"
      isLoading={isLoading}
      disabled={isLoading}
      readonly={readonly}
    />
  );
};

export default Languages; 